import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, Image, Plus, Trash2, Download, Eye, 
  Palette, Upload, Settings, ChevronDown, ChevronUp,
  GripVertical, X, Check, Globe, Layout, Layers
} from 'lucide-react';
import api from '../../lib/api';
import {
  PAGE_TYPES,
  CoverPageEditor,
  SectionsPageEditor,
  LetterPageEditor,
  TablesPageEditor,
  DataPageEditor,
  DocumentPageEditor,
  PageTypeSelector
} from './TemplateStudio/PageTypes';

// Translations
const translations = {
  ar: {
    pageTitle: 'استوديو القوالب',
    pageSubtitle: 'إنشاء مستندات احترافية متعددة الصفحات',
    preview: 'معاينة',
    createPdf: 'إنشاء PDF',
    generating: 'جاري الإنشاء...',
    tabs: {
      language: 'اللغة',
      template: 'القالب',
      design: 'التصميم',
      images: 'الصور',
      content: 'المحتوى',
      pages: 'الصفحات',
      header: 'الرأسية',
      footer: 'التذييل'
    },
    pages: {
      title: 'إدارة الصفحات',
      addPage: 'إضافة صفحة',
      noPages: 'لا توجد صفحات. أضف صفحة جديدة للبدء.',
      pageOrder: 'ترتيب الصفحات',
      dragToReorder: 'اسحب لإعادة الترتيب',
      types: {
        cover: 'صفحة الغلاف',
        sections: 'صفحة الأقسام',
        data: 'صفحة البيانات',
        letter: 'صفحة الرسالة',
        tables: 'صفحة الجداول'
      }
    },
    language: {
      title: 'اختر اللغة',
      arabic: 'العربية',
      english: 'English'
    },
    template: {
      title: 'اختر القالب',
      templates: {
        classic: 'كلاسيكي',
        classicDesc: 'تصميم احترافي تقليدي',
        modern: 'عصري',
        modernDesc: 'تصميم حديث وأنيق',
        minimal: 'بسيط',
        minimalDesc: 'تصميم نظيف ومختصر',
        corporate: 'رسمي',
        corporateDesc: 'تصميم للشركات الكبرى',
        technical: 'تقني',
        technicalDesc: 'تصميم للمنتجات التقنية',
        elegant: 'أنيق',
        elegantDesc: 'تصميم فاخر ومميز'
      }
    },
    colors: {
      title: 'اختيار الألوان',
      customColors: 'استخدام ألوان مخصصة',
      primary: 'اللون الرئيسي',
      secondary: 'اللون الثانوي',
      titleColor: 'لون العنوان',
      sectionColor: 'لون الأقسام'
    },
    logo: {
      title: 'الشعار',
      dropText: 'اسحب الشعار هنا أو انقر للاختيار',
      formats: 'PNG, JPG, SVG'
    },
    images: {
      title: 'صور المنتج',
      dropText: 'اسحب الصور هنا أو انقر للاختيار',
      unlimited: 'يمكنك إضافة عدد غير محدود من الصور',
      count: 'صور'
    },
    content: {
      productName: 'اسم المنتج',
      productNamePlaceholder: 'أدخل اسم المنتج',
      titleColor: 'لون العنوان',
      sectionColor: 'لون الأقسام',
      addItem: 'إضافة عنصر',
      addSection: 'إضافة قسم جديد',
      items: 'عناصر',
      inputPlaceholder: 'أدخل النص هنا...',
      colorSettings: 'إعدادات الألوان',
      defaultSections: {
        description: 'وصف المنتج',
        features: 'الميزات الرئيسية',
        applications: 'التطبيقات',
        specs: 'المواصفات الفنية'
      }
    },
    header: {
      title: 'إعدادات الرأسية',
      text: 'نص الرأسية',
      line1Placeholder: 'اسم الشركة',
      line2Placeholder: 'الشعار أو الوصف',
      barSettings: 'إعدادات الشريط',
      enableGradient: 'تفعيل التدرج اللوني',
      gradientIntensity: 'نسبة التدرج',
      barColor: 'لون الشريط',
      semiCircle: 'شكل نصف دائرة عند الشعار',
      enableWhiteBlend: 'دمج مع اللون الأبيض',
      blendDirection: 'اتجاه الدمج',
      showTopLine: 'إظهار خط فوق التذييل',
      blendDirections: {
        horizontal: 'أفقي',
        vertical: 'عمودي',
        diagonal_up: 'قطري للأعلى',
        diagonal_down: 'قطري للأسفل',
        from_corner: 'من الزوايا',
        from_center: 'من المركز'
      }
    },
    footer: {
      title: 'إعدادات التذييل',
      style: 'نمط التذييل',
      text: 'نص التذييل',
      styles: {
        oval: 'بيضاوي',
        ovalDesc: 'شعار داخل شكل بيضاوي',
        bar: 'شريط',
        barDesc: 'شعار مع شريط ملون',
        minimal: 'بسيط',
        minimalDesc: 'نص فقط بدون شعار'
      },
      line1Placeholder: 'العنوان',
      line2Placeholder: 'الهاتف والموقع',
      line3Placeholder: 'البريد والسجل التجاري',
      barSettings: 'إعدادات الشريط',
      enableGradient: 'تفعيل التدرج اللوني',
      gradientIntensity: 'نسبة التدرج',
      barColor: 'لون الشريط',
      semiCircle: 'شكل نصف دائرة عند الشعار',
      enableWhiteBlend: 'دمج مع اللون الأبيض',
      blendDirection: 'اتجاه الدمج',
      showTopLine: 'إظهار خط فوق التذييل',
      blendDirections: {
        horizontal: 'أفقي',
        vertical: 'عمودي',
        diagonal_up: 'قطري للأعلى',
        diagonal_down: 'قطري للأسفل',
        from_corner: 'من الزوايا',
        from_center: 'من المركز'
      }
    },
    miniPreview: 'معاينة مصغرة',
    sections: 'أقسام',
    colorPicker: {
      clickToChange: 'انقر لتغيير اللون',
      customColor: 'أو اختر لون مخصص',
      close: 'إغلاق'
    },
    success: {
      title: 'تم إنشاء الملف بنجاح!',
      subtitle: 'تم إنشاء صفحة بيانات المنتج بنجاح',
      download: 'تحميل الملف',
      close: 'إغلاق'
    },
    errors: {
      noName: 'يرجى إدخال اسم المنتج',
      failed: 'حدث خطأ أثناء إنشاء الملف'
    }
  },
  en: {
    pageTitle: 'Template Studio',
    pageSubtitle: 'Create professional multi-page documents',
    preview: 'Preview',
    createPdf: 'Create PDF',
    generating: 'Generating...',
    tabs: {
      language: 'Language',
      template: 'Template',
      design: 'Design',
      images: 'Images',
      content: 'Content',
      pages: 'Pages',
      header: 'Header',
      footer: 'Footer'
    },
    pages: {
      title: 'Page Management',
      addPage: 'Add Page',
      noPages: 'No pages yet. Add a page to get started.',
      pageOrder: 'Page Order',
      dragToReorder: 'Drag to reorder',
      types: {
        cover: 'Cover Page',
        sections: 'Sections Page',
        data: 'Data Page',
        letter: 'Letter Page',
        tables: 'Tables Page'
      }
    },
    language: {
      title: 'Select Language',
      arabic: 'العربية',
      english: 'English'
    },
    template: {
      title: 'Select Template',
      templates: {
        classic: 'Classic',
        classicDesc: 'Traditional professional design',
        modern: 'Modern',
        modernDesc: 'Contemporary and sleek',
        minimal: 'Minimal',
        minimalDesc: 'Clean and simple',
        corporate: 'Corporate',
        corporateDesc: 'Enterprise-level design',
        technical: 'Technical',
        technicalDesc: 'For technical products',
        elegant: 'Elegant',
        elegantDesc: 'Premium and distinctive'
      }
    },
    colors: {
      title: 'Color Selection',
      customColors: 'Use custom colors',
      primary: 'Primary Color',
      secondary: 'Secondary Color',
      titleColor: 'Title Color',
      sectionColor: 'Section Color'
    },
    logo: {
      title: 'Logo',
      dropText: 'Drag logo here or click to select',
      formats: 'PNG, JPG, SVG'
    },
    images: {
      title: 'Product Images',
      dropText: 'Drag images here or click to select',
      unlimited: 'You can add unlimited images',
      count: 'images'
    },
    content: {
      productName: 'Product Name',
      productNamePlaceholder: 'Enter product name',
      titleColor: 'Title Color',
      sectionColor: 'Section Color',
      addItem: 'Add Item',
      addSection: 'Add New Section',
      items: 'items',
      inputPlaceholder: 'Enter text here...',
      colorSettings: 'Color Settings',
      defaultSections: {
        description: 'Product Description',
        features: 'Key Features',
        applications: 'Applications',
        specs: 'Technical Specifications'
      }
    },
    header: {
      title: 'Header Settings',
      text: 'Header Text',
      line1Placeholder: 'Company Name',
      line2Placeholder: 'Tagline or Description',
      barSettings: 'Bar Settings',
      enableGradient: 'Enable Gradient',
      gradientIntensity: 'Gradient Intensity',
      barColor: 'Bar Color',
      semiCircle: 'Semi-circle at Logo',
      enableWhiteBlend: 'Blend with White',
      blendDirection: 'Blend Direction',
      showTopLine: 'Show Top Line',
      blendDirections: {
        horizontal: 'Horizontal',
        vertical: 'Vertical',
        diagonal_up: 'Diagonal Up',
        diagonal_down: 'Diagonal Down',
        from_corner: 'From Corner',
        from_center: 'From Center'
      }
    },
    footer: {
      title: 'Footer Settings',
      style: 'Footer Style',
      text: 'Footer Text',
      styles: {
        oval: 'Oval',
        ovalDesc: 'Logo inside oval shape',
        bar: 'Bar',
        barDesc: 'Logo with colored bar',
        minimal: 'Minimal',
        minimalDesc: 'Text only without logo'
      },
      line1Placeholder: 'Address',
      line2Placeholder: 'Phone and Website',
      line3Placeholder: 'Email and Registration',
      barSettings: 'Bar Settings',
      enableGradient: 'Enable Gradient',
      gradientIntensity: 'Gradient Intensity',
      barColor: 'Bar Color',
      semiCircle: 'Semi-circle at Logo',
      enableWhiteBlend: 'Blend with White',
      blendDirection: 'Blend Direction',
      showTopLine: 'Show Top Line',
      blendDirections: {
        horizontal: 'Horizontal',
        vertical: 'Vertical',
        diagonal_up: 'Diagonal Up',
        diagonal_down: 'Diagonal Down',
        from_corner: 'From Corner',
        from_center: 'From Center'
      }
    },
    miniPreview: 'Mini Preview',
    sections: 'sections',
    colorPicker: {
      clickToChange: 'Click to change color',
      customColor: 'Or choose custom color',
      close: 'Close'
    },
    success: {
      title: 'File Created Successfully!',
      subtitle: 'Product datasheet created successfully',
      download: 'Download File',
      close: 'Close'
    },
    errors: {
      noName: 'Please enter product name',
      failed: 'Error creating file'
    }
  }
};

// Color Palette for picker
const COLOR_PALETTE = [
  // Blues
  '#1A4D91', '#2563EB', '#3B82F6', '#0EA5E9', '#06B6D4',
  // Greens
  '#059669', '#10B981', '#22C55E', '#84CC16', '#1D8348',
  // Reds/Oranges
  '#DC2626', '#EF4444', '#F97316', '#F59E0B', '#A41F32',
  // Purples/Pinks
  '#7C3AED', '#8B5CF6', '#A855F7', '#D946EF', '#4A235A',
  // Grays/Blacks
  '#1F2937', '#374151', '#4B5563', '#6B7280', '#000000',
  // Others
  '#14532D', '#1E3A5F', '#7F1D1D', '#713F12', '#312E81',
];

// Template Definitions - Title color same as primary
const TEMPLATES = [
  {
    id: 'classic',
    colors: { primary: '#1A4D91', secondary: '#4D99D9' },
    headerStyle: 'swoosh',
    footerStyle: 'oval'
  },
  {
    id: 'modern',
    colors: { primary: '#2C3E50', secondary: '#3498DB' },
    headerStyle: 'gradient',
    footerStyle: 'bar'
  },
  {
    id: 'minimal',
    colors: { primary: '#333333', secondary: '#666666' },
    headerStyle: 'simple',
    footerStyle: 'minimal'
  },
  {
    id: 'corporate',
    colors: { primary: '#1B4F72', secondary: '#5DADE2' },
    headerStyle: 'corporate',
    footerStyle: 'oval'
  },
  {
    id: 'technical',
    colors: { primary: '#1D8348', secondary: '#58D68D' },
    headerStyle: 'technical',
    footerStyle: 'bar'
  },
  {
    id: 'elegant',
    colors: { primary: '#4A235A', secondary: '#9B59B6' },
    headerStyle: 'elegant',
    footerStyle: 'oval'
  }
];

// Color Picker Component
const ColorPicker = ({ color, onChange, label, translations: t }) => {
  const [showPicker, setShowPicker] = useState(false);
  
  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setShowPicker(!showPicker)}
        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50 w-full"
      >
        <div 
          className="w-10 h-10 rounded-lg border-2 border-gray-300 shadow-inner"
          style={{ backgroundColor: color }}
        />
        <span className="text-gray-600">{t?.colorPicker?.clickToChange || 'Click to change'}</span>
      </button>
      
      {showPicker && (
        <div className="absolute z-50 mt-2 p-4 bg-white rounded-xl shadow-2xl border w-72">
          <div className="grid grid-cols-5 gap-2 mb-3">
            {COLOR_PALETTE.map((c) => (
              <button
                key={c}
                onClick={() => {
                  onChange(c);
                  setShowPicker(false);
                }}
                className={`w-10 h-10 rounded-lg border-2 hover:scale-110 transition-transform ${
                  color === c ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200'
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 pt-3 border-t">
            <input
              type="color"
              value={color}
              onChange={(e) => onChange(e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border-0"
            />
            <span className="text-sm text-gray-500">{t?.colorPicker?.customColor || 'Or choose custom'}</span>
          </div>
          <button
            onClick={() => setShowPicker(false)}
            className="mt-3 w-full py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
          >
            {t?.colorPicker?.close || 'Close'}
          </button>
        </div>
      )}
    </div>
  );
};

const TemplateStudioPage = () => {
  // Language state
  const [language, setLanguage] = useState('ar');
  const t = translations[language];
  const isRTL = language === 'ar';
  
  // Template state
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATES[0]);
  
  // Colors state - Title color same as primary by default
  const [titleColor, setTitleColor] = useState('#1A4D91');
  const [sectionColor, setSectionColor] = useState('#1A4D91');
  const [customColors, setCustomColors] = useState({
    primary: '#1A4D91',
    secondary: '#4D99D9'
  });
  const [useCustomColors, setUseCustomColors] = useState(false);
  
  // Logo state
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Footer settings
  const [footerStyle, setFooterStyle] = useState('oval');
  const [footerText, setFooterText] = useState({
    line1: 'Hafsa Bint Omar Street | Al Rawdah Dist | Riyadh | Kingdom of Saudi Arabia',
    line2: 'Email: nicole@dhr-sa.com | CR: 7052942476 | VAT: 314477769800003',
    line3: 'Tel: +966 55 216 8158 | www.dhr-sa.com'
  });
  
  // Header settings
  const [headerText, setHeaderText] = useState({
    line1: 'DISTINCT HIGH ROAD',
    line2: 'Building Excellence | Quality Solutions'
  });
  
  // Bar settings for header/footer
  const [headerBarSettings, setHeaderBarSettings] = useState({
    enableGradient: false,
    gradientIntensity: 50,
    barColor: '#1A4D91',
    semiCircle: true,
    enableWhiteBlend: false,
    whiteBlendDirection: 'horizontal'
  });
  
  const [footerBarSettings, setFooterBarSettings] = useState({
    enableGradient: false,
    gradientIntensity: 50,
    barColor: '#1A4D91',
    semiCircle: true,
    enableWhiteBlend: false,
    whiteBlendDirection: 'horizontal',
    textColor: '#FFFFFF',
    textSize: 7,
    singleLine: false,
    showTopLine: true
  });
  
  // Images state
  const [images, setImages] = useState([]);
  
  // Content state
  const [productName, setProductName] = useState('');
  const [sections, setSections] = useState([
    { id: 1, title: t.content.defaultSections.description, items: [''], expanded: true },
    { id: 2, title: t.content.defaultSections.features, items: [''], expanded: true },
    { id: 3, title: t.content.defaultSections.applications, items: [''], expanded: true },
    { id: 4, title: t.content.defaultSections.specs, items: [''], expanded: true },
  ]);
  
  // Document Pages state - new multi-page system
  const [documentPages, setDocumentPages] = useState([]);
  const [showPageSelector, setShowPageSelector] = useState(false);
  
  // UI state
  const [activeTab, setActiveTab] = useState('language');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);

  // Page management functions
  const addPage = (pageType) => {
    const newPage = {
      id: Date.now(),
      type: pageType,
      data: getDefaultPageData(pageType)
    };
    setDocumentPages([...documentPages, newPage]);
    setShowPageSelector(false);
  };

  const getDefaultPageData = (pageType) => {
    switch (pageType) {
      case 'cover':
        return { title: '', subtitle: '' };
      case 'sections':
        return { pageTitle: '', sectionNames: [''] };
      case 'data':
        // Each data page has its own independent data
        return { 
          productName: '',
          sections: [
            { id: 1, title: isRTL ? 'وصف المنتج' : 'Product Description', items: [''] },
            { id: 2, title: isRTL ? 'الميزات الرئيسية' : 'Key Features', items: [''] },
          ],
          images: []
        };
      case 'letter':
        return { blocks: [{ date: '', title: '', text: '' }] };
      case 'tables':
        return { tables: [] };
      case 'document':
        return { 
          title: '',
          fileName: null,
          fileSize: null,
          fileType: null,
          fileData: null,
          previewUrl: null,
          showTitle: true,
          fitToPage: true
        };
      default:
        return {};
    }
  };

  const updatePage = (pageId, newData) => {
    setDocumentPages(documentPages.map(page =>
      page.id === pageId ? { ...page, data: newData } : page
    ));
  };

  const removePage = (pageId) => {
    setDocumentPages(documentPages.filter(page => page.id !== pageId));
  };

  const movePage = (pageId, direction) => {
    const index = documentPages.findIndex(p => p.id === pageId);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === documentPages.length - 1)
    ) return;
    
    const newPages = [...documentPages];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newPages[index], newPages[newIndex]] = [newPages[newIndex], newPages[index]];
    setDocumentPages(newPages);
  };

  // Update sections when language changes
  React.useEffect(() => {
    setSections([
      { id: 1, title: t.content.defaultSections.description, items: [''], expanded: true },
      { id: 2, title: t.content.defaultSections.features, items: [''], expanded: true },
      { id: 3, title: t.content.defaultSections.applications, items: [''], expanded: true },
      { id: 4, title: t.content.defaultSections.specs, items: [''], expanded: true },
    ]);
  }, [language]);

  // Logo dropzone
  const onLogoDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onload = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  }, []);

  const { getRootProps: getLogoRootProps, getInputProps: getLogoInputProps } = useDropzone({
    onDrop: onLogoDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.svg'] },
    maxFiles: 1
  });

  // Images dropzone
  const onImagesDrop = useCallback((acceptedFiles) => {
    const newImages = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const { getRootProps: getImagesRootProps, getInputProps: getImagesInputProps } = useDropzone({
    onDrop: onImagesDrop,
    accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp'] }
  });

  const removeImage = (id) => {
    setImages(prev => prev.filter(img => img.id !== id));
  };

  // Section management
  const addSection = () => {
    const newId = Math.max(...sections.map(s => s.id), 0) + 1;
    setSections([...sections, { 
      id: newId, 
      title: language === 'ar' ? 'قسم جديد' : 'New Section', 
      items: [''],
      expanded: true 
    }]);
  };

  const removeSection = (id) => {
    setSections(sections.filter(s => s.id !== id));
  };

  const updateSectionTitle = (id, title) => {
    setSections(sections.map(s => s.id === id ? { ...s, title } : s));
  };

  const toggleSection = (id) => {
    setSections(sections.map(s => s.id === id ? { ...s, expanded: !s.expanded } : s));
  };

  const addItem = (sectionId) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, items: [...s.items, ''] } : s
    ));
  };

  const removeItem = (sectionId, itemIndex) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, items: s.items.filter((_, i) => i !== itemIndex) } : s
    ));
  };

  const updateItem = (sectionId, itemIndex, value) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { 
        ...s, 
        items: s.items.map((item, i) => i === itemIndex ? value : item) 
      } : s
    ));
  };

  // Template selection
  const selectTemplate = (template) => {
    setSelectedTemplate(template);
    setCustomColors(template.colors);
    setTitleColor(template.colors.primary);
    setSectionColor(template.colors.primary);
    setFooterStyle(template.footerStyle);
    setUseCustomColors(false);
  };

  // Generate PDF
  const generatePdf = async () => {
    // Check if using multi-page system or traditional
    let pagesToGenerate = documentPages;
    
    // If no pages added, create a default cover page to show header/footer preview
    if (documentPages.length === 0) {
      pagesToGenerate = [{
        id: 'default-preview',
        type: 'cover',
        data: {
          title: productName || (isRTL ? 'معاينة المستند' : 'Document Preview'),
          subtitle: isRTL ? 'معاينة الرأسية والتذييل' : 'Header and Footer Preview',
          styles: {
            titleColor: titleColor,
            titleSize: 36,
            textColor: '#666666',
            textSize: 18
          }
        }
      }];
    }
    
    // Enrich data pages with styles (each page already has its own productName, sections, images)
    pagesToGenerate = pagesToGenerate.map(page => {
      if (page.type === 'data') {
        // Use page's own data (independent per page)
        const pageData = page.data || {};
        return {
          ...page,
          data: {
            ...pageData,
            productName: pageData.productName || '',
            sections: (pageData.sections || []).map(s => ({
              title: s.title,
              items: (s.items || []).filter(item => item && item.trim())
            })),
            images: pageData.images || [],
            styles: {
              titleColor: titleColor,
              sectionColor: sectionColor,
              textColor: '#333333',
              titleSize: 24,
              sectionTitleSize: 14,
              textSize: 11
            }
          }
        };
      }
      return page;
    });
    
    const useMultiPage = pagesToGenerate.length > 0;
    
    if (!useMultiPage && !productName.trim()) {
      alert(t.errors?.noName || 'Please enter a product name');
      return;
    }

    setIsGenerating(true);
    
    try {
      const formData = new FormData();
      
      if (useMultiPage) {
        // Use new multi-page document API
        formData.append('document_name', productName || 'Document');
        formData.append('language', language);
        formData.append('colors', JSON.stringify({
          ...( useCustomColors ? customColors : selectedTemplate.colors),
          title: titleColor,
          section: sectionColor
        }));
        formData.append('footer_text', JSON.stringify(footerText));
        formData.append('header_text', JSON.stringify(headerText));
        formData.append('header_bar_settings', JSON.stringify(headerBarSettings));
        formData.append('footer_bar_settings', JSON.stringify(footerBarSettings));
        formData.append('footer_style', footerStyle);
        
        if (logo) {
          formData.append('logo', logo);
        }
        
        // Collect images from all data pages and add them to formData
        // Also update pages data with image indices for backend reference
        let imageIndex = 0;
        const pagesWithImageRefs = pagesToGenerate.map((page, pageIdx) => {
          if (page.type === 'data' && page.data?.images?.length > 0) {
            const imageRefs = [];
            page.data.images.forEach((img) => {
              if (img.file) {
                const fieldName = `page_${pageIdx}_image_${imageIndex}`;
                formData.append(fieldName, img.file);
                imageRefs.push(fieldName);
                imageIndex++;
              }
            });
            return {
              ...page,
              data: {
                ...page.data,
                imageRefs: imageRefs  // Reference to uploaded images
              }
            };
          }
          return page;
        });
        
        formData.append('pages', JSON.stringify(pagesWithImageRefs));
        
        const response = await api.post('/templates/generate-document', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          setGeneratedPdf(response.data);
          setShowPreview(true);
        } else {
          alert((t.errors?.failed || 'Failed') + ': ' + response.data.error);
        }
      } else {
        // Use traditional datasheet API
        formData.append('product_name', productName);
        formData.append('language', language);
        formData.append('template_id', selectedTemplate.id);
        formData.append('colors', JSON.stringify({
          ...( useCustomColors ? customColors : selectedTemplate.colors),
          title: titleColor,
          section: sectionColor
        }));
        formData.append('footer_style', footerStyle);
        formData.append('footer_text', JSON.stringify(footerText));
        formData.append('header_text', JSON.stringify(headerText));
        formData.append('header_bar_settings', JSON.stringify(headerBarSettings));
        formData.append('footer_bar_settings', JSON.stringify(footerBarSettings));
        formData.append('sections', JSON.stringify(sections.map(s => ({
          title: s.title,
          items: s.items.filter(item => item.trim())
        }))));
        
        if (logo) {
          formData.append('logo', logo);
        }
        
        images.forEach((img) => {
          formData.append('images', img.file);
        });

        const response = await api.post('/templates/generate-datasheet', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (response.data.success) {
          setGeneratedPdf(response.data);
          setShowPreview(true);
        } else {
          alert((t.errors?.failed || 'Failed') + ': ' + response.data.error);
        }
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(t.errors?.failed || 'Failed to generate PDF');
    } finally {
      setIsGenerating(false);
    }
  };

  const activeColors = useCustomColors ? customColors : selectedTemplate.colors;

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">{t.pageTitle}</h1>
                <p className="text-sm text-gray-500">{t.pageSubtitle}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFullPreview(true)}
                className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                <Eye className="w-4 h-4" />
                {t.preview}
              </button>
              <button
                onClick={generatePdf}
                disabled={isGenerating || (documentPages.length === 0 && !productName.trim())}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isGenerating ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isGenerating ? t.generating : t.createPdf}
              </button>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {[
              { id: 'language', label: t.tabs.language, icon: Globe },
              { id: 'template', label: t.tabs.template, icon: Layout },
              { id: 'design', label: t.tabs.design, icon: Palette },
              { id: 'images', label: t.tabs.images, icon: Image },
              { id: 'pages', label: t.tabs.pages, icon: Layers },
              { id: 'content', label: t.tabs.content, icon: FileText },
              { id: 'header', label: t.tabs.header, icon: Settings },
              { id: 'footer', label: t.tabs.footer, icon: Settings },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Language Tab */}
            {activeTab === 'language' && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  {t.language.title}
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setLanguage('ar')}
                    className={`p-6 rounded-xl border-2 transition-all text-center ${
                      language === 'ar' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-4xl mb-3">🇸🇦</div>
                    <p className="text-xl font-bold">{t.language.arabic}</p>
                    <p className="text-sm text-gray-500 mt-1">Arabic / عربي</p>
                  </button>
                  
                  <button
                    onClick={() => setLanguage('en')}
                    className={`p-6 rounded-xl border-2 transition-all text-center ${
                      language === 'en' 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-4xl mb-3">🇬🇧</div>
                    <p className="text-xl font-bold">{t.language.english}</p>
                    <p className="text-sm text-gray-500 mt-1">English / إنجليزي</p>
                  </button>
                </div>
              </div>
            )}

            {/* Template Tab */}
            {activeTab === 'template' && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-6 flex items-center gap-2">
                  <Layout className="w-5 h-5 text-blue-600" />
                  {t.template.title}
                </h3>
                
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => selectTemplate(template)}
                      className={`p-4 rounded-xl border-2 transition-all ${
                        selectedTemplate.id === template.id 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {/* Template Preview */}
                      <div className="aspect-[3/4] bg-white rounded-lg border mb-3 overflow-hidden relative">
                        <div 
                          className="h-3 w-full"
                          style={{ backgroundColor: template.colors.primary }}
                        />
                        <div className="p-2">
                          <div 
                            className="h-2 w-16 rounded mb-2"
                            style={{ backgroundColor: template.colors.primary }}
                          />
                          <div 
                            className="h-3 w-24 rounded mb-3"
                            style={{ backgroundColor: template.colors.primary }}
                          />
                          <div className="space-y-1">
                            <div className="h-1 bg-gray-200 rounded w-full" />
                            <div className="h-1 bg-gray-200 rounded w-3/4" />
                            <div className="h-1 bg-gray-200 rounded w-5/6" />
                          </div>
                        </div>
                        <div 
                          className="absolute bottom-0 left-0 right-0 h-2"
                          style={{ backgroundColor: template.colors.primary }}
                        />
                      </div>
                      
                      {/* Color Dots */}
                      <div className="flex gap-1 mb-2 justify-center">
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: template.colors.primary }}
                        />
                        <div 
                          className="w-6 h-6 rounded-full border"
                          style={{ backgroundColor: template.colors.secondary }}
                        />
                      </div>
                      
                      <p className="font-semibold text-center">
                        {t.template.templates[template.id]}
                      </p>
                      <p className="text-xs text-gray-500 text-center">
                        {t.template.templates[template.id + 'Desc']}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Design Tab */}
            {activeTab === 'design' && (
              <div className="space-y-6">
                {/* Color Customization */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Palette className="w-5 h-5 text-blue-600" />
                    {t.colors.title}
                  </h3>
                  
                  {/* Current Template Colors */}
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg mb-4">
                    <span className="text-sm text-gray-600">
                      {t.template.templates[selectedTemplate.id]}:
                    </span>
                    <div className="flex gap-2">
                      <div 
                        className="w-10 h-10 rounded-lg border-2"
                        style={{ backgroundColor: selectedTemplate.colors.primary }}
                      />
                      <div 
                        className="w-10 h-10 rounded-lg border-2"
                        style={{ backgroundColor: selectedTemplate.colors.secondary }}
                      />
                    </div>
                  </div>
                  
                  {/* Custom Colors Toggle */}
                  <label className="flex items-center gap-2 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomColors}
                      onChange={(e) => setUseCustomColors(e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                    <span className="font-medium">{t.colors.customColors}</span>
                  </label>
                  
                  {useCustomColors && (
                    <div className="grid grid-cols-2 gap-4">
                      <ColorPicker
                        color={customColors.primary}
                        onChange={(c) => setCustomColors({...customColors, primary: c})}
                        label={t.colors.primary}
                        translations={t}
                      />
                      <ColorPicker
                        color={customColors.secondary}
                        onChange={(c) => setCustomColors({...customColors, secondary: c})}
                        label={t.colors.secondary}
                        translations={t}
                      />
                    </div>
                  )}
                </div>

                {/* Logo Upload */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-600" />
                    {t.logo.title}
                  </h3>
                  
                  <div 
                    {...getLogoRootProps()} 
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors"
                  >
                    <input {...getLogoInputProps()} />
                    {logoPreview ? (
                      <div className="relative inline-block">
                        <img src={logoPreview} alt="Logo" className="max-h-24 mx-auto" />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setLogo(null);
                            setLogoPreview(null);
                          }}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                        <p className="text-gray-600">{t.logo.dropText}</p>
                        <p className="text-sm text-gray-400 mt-1">{t.logo.formats}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Images Tab */}
            {activeTab === 'images' && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-600" />
                  {t.images.title}
                  <span className="text-sm font-normal text-gray-500">
                    ({images.length} {t.images.count})
                  </span>
                </h3>
                
                <div 
                  {...getImagesRootProps()} 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-400 transition-colors mb-4"
                >
                  <input {...getImagesInputProps()} />
                  <Image className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">{t.images.dropText}</p>
                  <p className="text-sm text-gray-400 mt-1">{t.images.unlimited}</p>
                </div>
                
                {images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {images.map((img, index) => (
                      <div key={img.id} className="relative group">
                        <img 
                          src={img.preview} 
                          alt={`Product ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <button
                            onClick={() => removeImage(img.id)}
                            className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Pages Tab - New Multi-page System */}
            {activeTab === 'pages' && (
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Layers className="w-5 h-5 text-blue-600" />
                      {t.pages?.title || 'Page Management'}
                    </h3>
                    <button
                      onClick={() => setShowPageSelector(!showPageSelector)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      {t.pages?.addPage || 'Add Page'}
                    </button>
                  </div>
                  
                  {/* Page Type Selector */}
                  {showPageSelector && (
                    <div className="mb-6">
                      <PageTypeSelector onAdd={addPage} isRTL={isRTL} />
                    </div>
                  )}
                  
                  {/* Pages List */}
                  {documentPages.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Layers className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{t.pages?.noPages || 'No pages yet. Add a page to get started.'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {documentPages.map((page, index) => {
                        const pageType = PAGE_TYPES[page.type];
                        const Icon = pageType?.icon || FileText;
                        
                        return (
                          <div key={page.id} className="border rounded-lg overflow-hidden">
                            {/* Page Header */}
                            <div className="flex items-center justify-between p-3 bg-gray-50 border-b">
                              <div className="flex items-center gap-3">
                                <div className="flex flex-col gap-1">
                                  <button
                                    onClick={() => movePage(page.id, 'up')}
                                    disabled={index === 0}
                                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => movePage(page.id, 'down')}
                                    disabled={index === documentPages.length - 1}
                                    className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </button>
                                </div>
                                <span className="text-gray-400 font-mono text-sm">{index + 1}</span>
                                <Icon className="w-5 h-5 text-gray-600" />
                                <span className="font-medium">
                                  {isRTL ? pageType?.labelAr : pageType?.labelEn}
                                </span>
                              </div>
                              <button
                                onClick={() => removePage(page.id)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            
                            {/* Page Editor */}
                            <div className="p-4">
                              {page.type === 'cover' && (
                                <CoverPageEditor
                                  page={page.data}
                                  onChange={(data) => updatePage(page.id, data)}
                                  isRTL={isRTL}
                                  t={t}
                                />
                              )}
                              {page.type === 'sections' && (
                                <SectionsPageEditor
                                  page={page.data}
                                  onChange={(data) => updatePage(page.id, data)}
                                  isRTL={isRTL}
                                  t={t}
                                />
                              )}
                              {page.type === 'data' && (
                                <DataPageEditor
                                  page={page.data}
                                  pageId={page.id}
                                  onChange={(data) => updatePage(page.id, data)}
                                  isRTL={isRTL}
                                />
                              )}
                              {page.type === 'letter' && (
                                <LetterPageEditor
                                  page={page.data}
                                  onChange={(data) => updatePage(page.id, data)}
                                  isRTL={isRTL}
                                  t={t}
                                />
                              )}
                              {page.type === 'tables' && (
                                <TablesPageEditor
                                  page={page.data}
                                  onChange={(data) => updatePage(page.id, data)}
                                  isRTL={isRTL}
                                  t={t}
                                />
                              )}
                              {page.type === 'document' && (
                                <DocumentPageEditor
                                  page={{ data: page.data }}
                                  onChange={(updated) => updatePage(page.id, updated.data)}
                                  isRTL={isRTL}
                                />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Content Tab */}
            {activeTab === 'content' && (
              <div className="space-y-4">
                {/* Product Name with Color */}
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className={`flex items-center justify-between mb-4 ${isRTL ? '' : 'flex-row-reverse'}`}>
                    <label className="text-lg font-semibold">{t.content.productName}</label>
                  </div>
                  <input
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder={t.content.productNamePlaceholder}
                    className={`w-full px-4 py-3 border rounded-lg text-lg mb-4 ${isRTL ? 'text-right' : 'text-left'}`}
                    style={{ color: titleColor }}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  
                  {/* Color Settings */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-4 flex items-center gap-2">
                      <Palette className="w-4 h-4" />
                      {t.content.colorSettings}
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <ColorPicker
                        color={titleColor}
                        onChange={setTitleColor}
                        label={t.content.titleColor}
                        translations={t}
                      />
                      <ColorPicker
                        color={sectionColor}
                        onChange={setSectionColor}
                        label={t.content.sectionColor}
                        translations={t}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Sections */}
                {sections.map((section) => (
                  <div key={section.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer"
                      style={{ backgroundColor: sectionColor + '15' }}
                      onClick={() => toggleSection(section.id)}
                    >
                      <div className={`flex items-center gap-3 ${isRTL ? 'flex-row' : 'flex-row'}`}>
                        <GripVertical className="w-5 h-5 text-gray-400" />
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: sectionColor }}
                        />
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateSectionTitle(section.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className={`font-semibold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 outline-none ${isRTL ? 'text-right' : 'text-left'}`}
                          style={{ color: sectionColor }}
                          dir={isRTL ? 'rtl' : 'ltr'}
                        />
                        <span className="text-sm text-gray-500">
                          ({section.items.filter(i => i.trim()).length} {t.content.items})
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeSection(section.id);
                          }}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                        {section.expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </div>
                    </div>
                    
                    {section.expanded && (
                      <div className="p-4 space-y-2">
                        {section.items.map((item, itemIndex) => (
                          <div key={itemIndex} className={`flex items-center gap-2 ${isRTL ? 'flex-row' : 'flex-row'}`}>
                            <span 
                              className="w-6 h-6 flex items-center justify-center rounded-full text-sm text-white flex-shrink-0"
                              style={{ backgroundColor: sectionColor }}
                            >
                              {itemIndex + 1}
                            </span>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => updateItem(section.id, itemIndex, e.target.value)}
                              placeholder={t.content.inputPlaceholder}
                              className={`flex-1 px-3 py-2 border rounded-lg ${isRTL ? 'text-right' : 'text-left'}`}
                              dir={isRTL ? 'rtl' : 'ltr'}
                            />
                            <button
                              onClick={() => removeItem(section.id, itemIndex)}
                              className="p-2 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                              disabled={section.items.length === 1}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                        <button
                          onClick={() => addItem(section.id)}
                          className="flex items-center gap-2 px-4 py-2 hover:bg-blue-50 rounded-lg w-full justify-center border border-dashed"
                          style={{ borderColor: sectionColor, color: sectionColor }}
                        >
                          <Plus className="w-4 h-4" />
                          {t.content.addItem}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
                
                <button
                  onClick={addSection}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full justify-center"
                >
                  <Plus className="w-5 h-5" />
                  {t.content.addSection}
                </button>
              </div>
            )}

            {/* Header Tab */}
            {activeTab === 'header' && (
              <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  {t.header.title}
                </h3>
                
                {/* Header Text */}
                <div className="space-y-3">
                  <label className="block font-medium">{t.header.text}</label>
                  <input
                    type="text"
                    value={headerText.line1}
                    onChange={(e) => setHeaderText({...headerText, line1: e.target.value})}
                    placeholder={t.header.line1Placeholder}
                    className="w-full px-4 py-2 border rounded-lg"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  <input
                    type="text"
                    value={headerText.line2}
                    onChange={(e) => setHeaderText({...headerText, line2: e.target.value})}
                    placeholder={t.header.line2Placeholder}
                    className="w-full px-4 py-2 border rounded-lg"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                
                {/* Bar Settings */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-4">{t.header.barSettings}</h4>
                  
                  {/* Semi-circle Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={headerBarSettings.semiCircle}
                      onChange={(e) => setHeaderBarSettings({...headerBarSettings, semiCircle: e.target.checked})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.header.semiCircle}</span>
                  </label>
                  
                  {/* Gradient Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={headerBarSettings.enableGradient}
                      onChange={(e) => setHeaderBarSettings({...headerBarSettings, enableGradient: e.target.checked, enableWhiteBlend: false})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.header.enableGradient}</span>
                  </label>
                  
                  {headerBarSettings.enableGradient && (
                    /* Gradient Intensity Slider */
                    <div className="space-y-2 mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">
                        {t.header.gradientIntensity}: {headerBarSettings.gradientIntensity}%
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={headerBarSettings.gradientIntensity}
                        onChange={(e) => setHeaderBarSettings({...headerBarSettings, gradientIntensity: parseInt(e.target.value)})}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>10%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  )}
                  
                  {/* White Blend Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={headerBarSettings.enableWhiteBlend}
                      onChange={(e) => setHeaderBarSettings({...headerBarSettings, enableWhiteBlend: e.target.checked, enableGradient: false})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.header.enableWhiteBlend}</span>
                  </label>
                  
                  {headerBarSettings.enableWhiteBlend && (
                    /* Blend Direction Selector */
                    <div className="space-y-2 mb-4 p-3 bg-blue-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.header.blendDirection}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: 'horizontal', label: t.header.blendDirections.horizontal },
                          { id: 'vertical', label: t.header.blendDirections.vertical },
                          { id: 'diagonal_up', label: t.header.blendDirections.diagonal_up },
                          { id: 'diagonal_down', label: t.header.blendDirections.diagonal_down },
                          { id: 'from_corner', label: t.header.blendDirections.from_corner },
                          { id: 'from_center', label: t.header.blendDirections.from_center },
                        ].map(dir => (
                          <button
                            key={dir.id}
                            onClick={() => setHeaderBarSettings({...headerBarSettings, whiteBlendDirection: dir.id})}
                            className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                              headerBarSettings.whiteBlendDirection === dir.id
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {dir.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Bar Color Picker - only when no gradient or white blend */}
                  {!headerBarSettings.enableGradient && !headerBarSettings.enableWhiteBlend && (
                    <ColorPicker
                      color={headerBarSettings.barColor}
                      onChange={(c) => setHeaderBarSettings({...headerBarSettings, barColor: c})}
                      label={t.header.barColor}
                      translations={t}
                    />
                  )}
                  
                  {/* Bar Color Picker - when white blend is enabled */}
                  {headerBarSettings.enableWhiteBlend && (
                    <div className="mt-4">
                      <ColorPicker
                        color={headerBarSettings.barColor}
                        onChange={(c) => setHeaderBarSettings({...headerBarSettings, barColor: c})}
                        label={t.header.barColor}
                        translations={t}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Footer Tab */}
            {activeTab === 'footer' && (
              <div className="bg-white rounded-xl p-6 shadow-sm space-y-6">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Settings className="w-5 h-5 text-blue-600" />
                  {t.footer.title}
                </h3>
                
                <div>
                  <label className="block font-medium mb-3">{t.footer.style}</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'oval', label: t.footer.styles.oval, desc: t.footer.styles.ovalDesc },
                      { id: 'bar', label: t.footer.styles.bar, desc: t.footer.styles.barDesc },
                      { id: 'minimal', label: t.footer.styles.minimal, desc: t.footer.styles.minimalDesc },
                    ].map(style => (
                      <button
                        key={style.id}
                        onClick={() => setFooterStyle(style.id)}
                        className={`p-4 rounded-lg border-2 transition-all ${
                          footerStyle === style.id 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <p className="font-medium">{style.label}</p>
                        <p className="text-sm text-gray-500">{style.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <label className="block font-medium">{t.footer.text}</label>
                  <input
                    type="text"
                    value={footerText.line1}
                    onChange={(e) => setFooterText({...footerText, line1: e.target.value})}
                    placeholder={t.footer.line1Placeholder}
                    className="w-full px-4 py-2 border rounded-lg"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  <input
                    type="text"
                    value={footerText.line2}
                    onChange={(e) => setFooterText({...footerText, line2: e.target.value})}
                    placeholder={t.footer.line2Placeholder}
                    className="w-full px-4 py-2 border rounded-lg"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                  <input
                    type="text"
                    value={footerText.line3}
                    onChange={(e) => setFooterText({...footerText, line3: e.target.value})}
                    placeholder={t.footer.line3Placeholder}
                    className="w-full px-4 py-2 border rounded-lg"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  />
                </div>
                
                {/* Bar Settings */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-4">{t.footer.barSettings}</h4>
                  
                  {/* Semi-circle Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={footerBarSettings.semiCircle}
                      onChange={(e) => setFooterBarSettings({...footerBarSettings, semiCircle: e.target.checked})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.footer.semiCircle}</span>
                  </label>
                  
                  {/* Top Line Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={footerBarSettings.showTopLine}
                      onChange={(e) => setFooterBarSettings({...footerBarSettings, showTopLine: e.target.checked})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.footer.showTopLine}</span>
                  </label>
                  
                  {/* Gradient Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={footerBarSettings.enableGradient}
                      onChange={(e) => setFooterBarSettings({...footerBarSettings, enableGradient: e.target.checked, enableWhiteBlend: false})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.footer.enableGradient}</span>
                  </label>
                  
                  {footerBarSettings.enableGradient && (
                    /* Gradient Intensity Slider */
                    <div className="space-y-2 mb-4 p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">
                        {t.footer.gradientIntensity}: {footerBarSettings.gradientIntensity}%
                      </label>
                      <input
                        type="range"
                        min="10"
                        max="100"
                        value={footerBarSettings.gradientIntensity}
                        onChange={(e) => setFooterBarSettings({...footerBarSettings, gradientIntensity: parseInt(e.target.value)})}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>10%</span>
                        <span>100%</span>
                      </div>
                    </div>
                  )}
                  
                  {/* White Blend Toggle */}
                  <label className="flex items-center gap-3 mb-4 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={footerBarSettings.enableWhiteBlend}
                      onChange={(e) => setFooterBarSettings({...footerBarSettings, enableWhiteBlend: e.target.checked, enableGradient: false})}
                      className="w-5 h-5 rounded"
                    />
                    <span>{t.footer.enableWhiteBlend}</span>
                  </label>
                  
                  {footerBarSettings.enableWhiteBlend && (
                    /* Blend Direction Selector */
                    <div className="space-y-2 mb-4 p-3 bg-blue-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t.footer.blendDirection}
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { id: 'horizontal', label: t.footer.blendDirections.horizontal },
                          { id: 'vertical', label: t.footer.blendDirections.vertical },
                          { id: 'diagonal_up', label: t.footer.blendDirections.diagonal_up },
                          { id: 'diagonal_down', label: t.footer.blendDirections.diagonal_down },
                          { id: 'from_corner', label: t.footer.blendDirections.from_corner },
                          { id: 'from_center', label: t.footer.blendDirections.from_center },
                        ].map(dir => (
                          <button
                            key={dir.id}
                            onClick={() => setFooterBarSettings({...footerBarSettings, whiteBlendDirection: dir.id})}
                            className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                              footerBarSettings.whiteBlendDirection === dir.id
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                            }`}
                          >
                            {dir.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Bar Color Picker - only when no gradient or white blend */}
                  {!footerBarSettings.enableGradient && !footerBarSettings.enableWhiteBlend && (
                    <ColorPicker
                      color={footerBarSettings.barColor}
                      onChange={(c) => setFooterBarSettings({...footerBarSettings, barColor: c})}
                      label={t.footer.barColor}
                      translations={t}
                    />
                  )}
                  
                  {/* Bar Color Picker - when white blend is enabled */}
                  {footerBarSettings.enableWhiteBlend && (
                    <div className="mt-4">
                      <ColorPicker
                        color={footerBarSettings.barColor}
                        onChange={(c) => setFooterBarSettings({...footerBarSettings, barColor: c})}
                        label={t.footer.barColor}
                        translations={t}
                      />
                    </div>
                  )}
                  
                  {/* Footer Text Styling */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">{isRTL ? 'تنسيق نص التذييل' : 'Footer Text Styling'}</h4>
                    
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {/* Text Color */}
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          {isRTL ? 'لون النص' : 'Text Color'}
                        </label>
                        <input
                          type="color"
                          value={footerBarSettings.textColor || '#FFFFFF'}
                          onChange={(e) => setFooterBarSettings({...footerBarSettings, textColor: e.target.value})}
                          className="w-full h-10 border rounded cursor-pointer"
                        />
                      </div>
                      
                      {/* Text Size */}
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">
                          {isRTL ? 'حجم النص' : 'Text Size'}
                        </label>
                        <input
                          type="number"
                          min="6"
                          max="14"
                          value={footerBarSettings.textSize || 7}
                          onChange={(e) => setFooterBarSettings({...footerBarSettings, textSize: parseInt(e.target.value) || 7})}
                          className="w-full px-3 py-2 border rounded"
                        />
                      </div>
                    </div>
                    
                    {/* Single Line Toggle */}
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={footerBarSettings.singleLine || false}
                        onChange={(e) => setFooterBarSettings({...footerBarSettings, singleLine: e.target.checked})}
                        className="w-5 h-5 rounded"
                      />
                      <span>{isRTL ? 'سطر واحد للتذييل' : 'Single Line Footer'}</span>
                    </label>
                  </div>
                  
                  {/* Quick Color Presets for Bar */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">{isRTL ? 'ألوان سريعة للشريط' : 'Quick Bar Colors'}</h4>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { color: '#FFFFFF', label: isRTL ? 'أبيض' : 'White' },
                        { color: '#1A4D91', label: isRTL ? 'أزرق' : 'Blue' },
                        { color: '#333333', label: isRTL ? 'رمادي داكن' : 'Dark Gray' },
                        { color: '#000000', label: isRTL ? 'أسود' : 'Black' },
                        { color: '#2E7D32', label: isRTL ? 'أخضر' : 'Green' },
                        { color: '#C62828', label: isRTL ? 'أحمر' : 'Red' },
                      ].map(preset => (
                        <button
                          key={preset.color}
                          onClick={() => setFooterBarSettings({...footerBarSettings, barColor: preset.color})}
                          className={`px-3 py-1 text-xs rounded border transition-all ${
                            footerBarSettings.barColor === preset.color
                              ? 'ring-2 ring-blue-500'
                              : ''
                          }`}
                          style={{ 
                            backgroundColor: preset.color,
                            color: preset.color === '#FFFFFF' || preset.color === '#FFD700' ? '#333' : '#FFF',
                            borderColor: preset.color === '#FFFFFF' ? '#ccc' : preset.color
                          }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Stats & Generate Button */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-4 shadow-sm sticky top-32">
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="font-bold text-blue-600">{images.length}</div>
                  <div className="text-gray-500">{t.images.count}</div>
                </div>
                <div className="bg-gray-50 rounded p-2 text-center">
                  <div className="font-bold text-blue-600">{sections.length}</div>
                  <div className="text-gray-500">{t.sections}</div>
                </div>
              </div>
              
              {/* Pages Count */}
              {documentPages.length > 0 && (
                <div className="mt-2 bg-blue-50 rounded p-2 text-center">
                  <div className="font-bold text-blue-600">{documentPages.length}</div>
                  <div className="text-gray-500">{isRTL ? 'صفحات' : 'Pages'}</div>
                </div>
              )}
              
              {/* Generate Button */}
              <button
                onClick={generatePdf}
                disabled={isGenerating || (documentPages.length === 0 && !productName.trim())}
                className="w-full mt-4 flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {t.generating}
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    {t.createPdf}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showPreview && generatedPdf && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">{t.success.title}</h3>
              <button onClick={() => setShowPreview(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="text-lg font-semibold mb-2">{generatedPdf.filename}</h4>
              <p className="text-gray-500 mb-6">{t.success.subtitle}</p>
              
              <div className="flex gap-3 justify-center">
                <a
                  href={generatedPdf.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-5 h-5" />
                  {t.success.download}
                </a>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                >
                  {t.success.close}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Preview Modal */}
      {showFullPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">{t.preview}</h3>
              <button onClick={() => setShowFullPreview(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-auto max-h-[calc(90vh-120px)]">
              {/* Full Preview Content */}
              <div 
                className="bg-white border rounded-lg shadow-lg mx-auto"
                style={{ width: '100%', maxWidth: '595px', minHeight: '842px' }}
                dir={isRTL ? 'rtl' : 'ltr'}
              >
                {/* Header - Logo (white bg) + Bar with optional gradient and semi-circle */}
                <div className="p-4 border-b-2" style={{ borderColor: activeColors.primary }}>
                  <div className="flex items-center gap-0">
                    {/* Logo area - white background */}
                    <div className="bg-white p-2 flex-shrink-0 relative z-10">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="h-12" />
                      ) : (
                        <div 
                          className="text-2xl font-bold"
                          style={{ color: activeColors.primary }}
                        >
                          DHR
                        </div>
                      )}
                    </div>
                    {/* Bar with optional gradient or white blend */}
                    <div 
                      className="flex-1 px-4 py-3 relative"
                      style={{ 
                        background: headerBarSettings.enableGradient 
                          ? `linear-gradient(${isRTL ? 'to left' : 'to right'}, ${activeColors.secondary} 0%, ${activeColors.primary} ${headerBarSettings.gradientIntensity}%)`
                          : headerBarSettings.enableWhiteBlend
                          ? `linear-gradient(${
                              headerBarSettings.whiteBlendDirection === 'horizontal' ? (isRTL ? 'to left' : 'to right') :
                              headerBarSettings.whiteBlendDirection === 'vertical' ? 'to bottom' :
                              headerBarSettings.whiteBlendDirection === 'diagonal_up' ? 'to top right' :
                              headerBarSettings.whiteBlendDirection === 'diagonal_down' ? 'to bottom right' :
                              headerBarSettings.whiteBlendDirection === 'from_center' ? 'circle at center' :
                              'to right'
                            }, white, ${headerBarSettings.barColor})`
                          : headerBarSettings.barColor,
                        borderTopLeftRadius: headerBarSettings.semiCircle && !isRTL ? '25px' : '0',
                        borderBottomLeftRadius: headerBarSettings.semiCircle && !isRTL ? '25px' : '0',
                        borderTopRightRadius: headerBarSettings.semiCircle && isRTL ? '25px' : '0',
                        borderBottomRightRadius: headerBarSettings.semiCircle && isRTL ? '25px' : '0',
                        marginLeft: headerBarSettings.semiCircle && !isRTL ? '-15px' : '0',
                        marginRight: headerBarSettings.semiCircle && isRTL ? '-15px' : '0',
                      }}
                    >
                      <p className="font-bold text-sm text-white">{headerText.line1}</p>
                      <p className="text-xs opacity-90 text-white">{headerText.line2}</p>
                    </div>
                  </div>
                </div>
                
                {/* Title - moved down with spacing */}
                <div className="px-6 pt-6">
                  <h1 
                    className={`text-3xl font-bold ${isRTL ? 'text-right' : 'text-left'}`}
                    style={{ color: titleColor }}
                  >
                    {productName || t.content.productNamePlaceholder}
                  </h1>
                </div>
                
                {/* Spacing - 2 empty lines */}
                <div className="h-8"></div>
                
                {/* Content */}
                <div className="px-6 pb-6">
                  <div className={`flex gap-6 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                    <div className="flex-1 space-y-4">
                      {sections.map(section => (
                        <div key={section.id} className={isRTL ? 'text-right' : 'text-left'}>
                          <h3 
                            className="font-bold text-lg mb-2"
                            style={{ color: sectionColor }}
                          >
                            {section.title}
                          </h3>
                          <ul className="space-y-1">
                            {section.items.filter(i => i.trim()).map((item, idx) => (
                              <li key={idx} className={`flex items-start gap-2 text-sm text-gray-600 ${isRTL ? 'flex-row-reverse' : 'flex-row'}`}>
                                <span 
                                  className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                                  style={{ backgroundColor: sectionColor }}
                                />
                                <span className={isRTL ? 'text-right' : 'text-left'}>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                    
                    {/* Images - Fill completely, no border */}
                    {images.length > 0 && (
                      <div className="w-1/3 space-y-2">
                        {images.slice(0, 4).map((img, idx) => (
                          <div 
                            key={idx}
                            className="overflow-hidden"
                            style={{ height: '90px' }}
                          >
                            <img 
                              src={img.preview}
                              alt={`Product ${idx + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Footer - Logo (white bg) + Bar with optional gradient and semi-circle */}
                <div className="mt-auto border-t-2 p-4" style={{ borderColor: activeColors.primary }}>
                  <div className="flex items-center gap-0">
                    {/* Logo area - white background */}
                    <div className="bg-white p-2 flex-shrink-0 relative z-10">
                      {logoPreview ? (
                        <img src={logoPreview} alt="Logo" className="h-10" />
                      ) : (
                        <div 
                          className="text-xl font-bold"
                          style={{ color: activeColors.primary }}
                        >
                          DHR
                        </div>
                      )}
                    </div>
                    {/* Bar with optional gradient or white blend */}
                    <div 
                      className="flex-1 px-4 py-2"
                      style={{ 
                        background: footerBarSettings.enableGradient 
                          ? `linear-gradient(${isRTL ? 'to left' : 'to right'}, ${activeColors.secondary} 0%, ${activeColors.primary} ${footerBarSettings.gradientIntensity}%)`
                          : footerBarSettings.enableWhiteBlend
                          ? `linear-gradient(${
                              footerBarSettings.whiteBlendDirection === 'horizontal' ? (isRTL ? 'to left' : 'to right') :
                              footerBarSettings.whiteBlendDirection === 'vertical' ? 'to bottom' :
                              footerBarSettings.whiteBlendDirection === 'diagonal_up' ? 'to top right' :
                              footerBarSettings.whiteBlendDirection === 'diagonal_down' ? 'to bottom right' :
                              footerBarSettings.whiteBlendDirection === 'from_center' ? 'circle at center' :
                              'to right'
                            }, white, ${footerBarSettings.barColor})`
                          : footerBarSettings.barColor,
                        borderTopLeftRadius: footerBarSettings.semiCircle && !isRTL ? '25px' : '0',
                        borderBottomLeftRadius: footerBarSettings.semiCircle && !isRTL ? '25px' : '0',
                        borderTopRightRadius: footerBarSettings.semiCircle && isRTL ? '25px' : '0',
                        borderBottomRightRadius: footerBarSettings.semiCircle && isRTL ? '25px' : '0',
                        marginLeft: footerBarSettings.semiCircle && !isRTL ? '-15px' : '0',
                        marginRight: footerBarSettings.semiCircle && isRTL ? '-15px' : '0',
                      }}
                    >
                      <p className="text-xs text-white">{footerText.line1}</p>
                      <p className="text-xs text-white">{footerText.line2}</p>
                      <p className="text-xs text-white">{footerText.line3}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowFullPreview(false)}
                className="px-6 py-2 border rounded-lg hover:bg-gray-50"
              >
                {t.success.close}
              </button>
              <button
                onClick={() => {
                  setShowFullPreview(false);
                  generatePdf();
                }}
                disabled={!productName.trim()}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                {t.createPdf}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateStudioPage;
